import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { constructPanelForMessagePath } from "@/shared/components/visualization/DndContainer/dropHandlers";
import {
  isViewableAsMap,
  isViewableAsPlot,
  isViewableAsImage,
  isViewableAsRawMessage,
  isViewableAsConsoleLog,
  isViewableAsThreeD,
} from "@/shared/components/visualization/Panel/predicates";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  collectLeafNodes,
  type MessagePathNode,
  type TopicNode,
} from "@/shared/domain/topics";
import {
  DataObjectIcon,
  ImageIcon,
  ListAltIcon,
  MapIcon,
  TimelineIcon,
  ViewInArIcon,
} from "@/shared/foundation/icons/mui";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { PanelType, useVizDispatch } from "@/shared/state/visualization";

interface ContextMenuProps {
  node: MessagePathNode;
  anchorElement: HTMLElement | null;
  closeMenu: () => void;
}

interface MenuItemProps {
  key: string;
  onClick: () => void;
  icon: JSX.Element;
  text: string;
}

function createMenuItem({
  key,
  onClick,
  icon,
  text,
}: MenuItemProps): JSX.Element {
  return (
    <MenuItem key={key} onClick={onClick}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>
        <RobotoTypography
          sx={{
            fontSize: "0.85rem",
          }}
        >
          {text}
        </RobotoTypography>
      </ListItemText>
    </MenuItem>
  );
}

export function ContextMenu({
  node,
  anchorElement,
  closeMenu,
}: ContextMenuProps) {
  const dispatch = useVizDispatch();
  // Authoring only, as the entity settings gate it: a layout that already
  // holds a cloud still draws it with the flag off.
  const pointCloudsEnabled = useCheckFeatureFlag("release-point-clouds");

  /**
   * Due to the use of the `isViewableAs...` predicates, this error callback _should_ never be called.
   */
  const onError = (err: unknown) => {
    ErrorMonitoringService.captureError(err);
  };

  const menuItems = [];
  if (pointCloudsEnabled && isViewableAsThreeD(node)) {
    menuItems.push(
      showInThreeDPanelItem(() => {
        closeMenu();
        constructPanelForMessagePath(dispatch, node, PanelType.ThreeD, onError);
      }),
    );
  }

  if (isViewableAsImage(node)) {
    const handleImagePanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Image, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "image",
        onClick: handleImagePanelOption,
        icon: <ImageIcon fontSize="small" />,
        text: "View in Image panel",
      }),
    );
  }

  if (isViewableAsConsoleLog(node)) {
    const handleLogPanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Log, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "log",
        onClick: handleLogPanelOption,
        icon: <ListAltIcon fontSize="small" />,
        text: "View in Log panel",
      }),
    );
  }

  if (isViewableAsMap(node)) {
    const handleMapPanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Map, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "map",
        onClick: handleMapPanelOption,
        icon: <MapIcon fontSize="small" />,
        text: "View in Map panel",
      }),
    );
  }

  if (isViewableAsPlot(node)) {
    const handlePlotPanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(dispatch, node, PanelType.Plot, onError);
    };

    menuItems.push(
      createMenuItem({
        key: "plot",
        onClick: handlePlotPanelOption,
        icon: <TimelineIcon fontSize="small" />,
        text: "View in Plot panel",
      }),
    );
  }

  if (isViewableAsRawMessage(node)) {
    const handleRawMessagePanelOption = () => {
      closeMenu();
      constructPanelForMessagePath(
        dispatch,
        node,
        PanelType.RawMessage,
        onError,
      );
    };

    menuItems.push(
      createMenuItem({
        key: "raw",
        onClick: handleRawMessagePanelOption,
        icon: <DataObjectIcon fontSize="small" />,
        text: "View in Raw panel",
      }),
    );
  }

  return (
    <Menu
      id="context-menu"
      anchorEl={anchorElement}
      open={menuItems.length > 0 && anchorElement !== null}
      onClose={closeMenu}
    >
      {menuItems}
    </Menu>
  );
}

function showInThreeDPanelItem(onClick: () => void): JSX.Element {
  return createMenuItem({
    key: "three-d",
    onClick,
    icon: <ViewInArIcon fontSize="small" />,
    text: "View in 3D panel",
  });
}

interface TopicContextMenuProps {
  topic: TopicNode;
  anchorElement: HTMLElement | null;
  closeMenu: () => void;
}

/**
 * The menu on a topic's own row. A topic as a whole is shown in a panel only
 * when it is a point cloud, whose `data` is what the 3D panel draws; any other
 * topic has nothing here, and the menu stays closed.
 */
export function TopicContextMenu({
  topic,
  anchorElement,
  closeMenu,
}: TopicContextMenuProps) {
  const dispatch = useVizDispatch();
  const pointCloudsEnabled = useCheckFeatureFlag("release-point-clouds");
  // Once per topic, not per render: the leaf walk is over every path.
  const cloudData = React.useMemo(
    () => collectLeafNodes(topic).find(isViewableAsThreeD),
    [topic],
  );

  const onError = (err: unknown) => {
    ErrorMonitoringService.captureError(err);
  };

  const menuItems = [];
  if (pointCloudsEnabled && cloudData) {
    menuItems.push(
      showInThreeDPanelItem(() => {
        closeMenu();
        constructPanelForMessagePath(
          dispatch,
          cloudData,
          PanelType.ThreeD,
          onError,
        );
      }),
    );
  }

  return (
    <Menu
      id="context-menu"
      anchorEl={anchorElement}
      open={menuItems.length > 0 && anchorElement !== null}
      onClose={closeMenu}
    >
      {menuItems}
    </Menu>
  );
}
